import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { Overlay } from '~/ui/overlay/Overlay';
import { Modal } from '~/ui/modal/Modal';
import CloseIcon from '~/assets/icons/close.svg';

const StyledModal = styled(Modal)`
  border-radius: 24px !important;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    opacity: 0.7;
  }
`;

const StyledCloseIcon = styled(CloseIcon)`
  width: 20px;
  height: 20px;
`;

const Title = styled.h3`
  margin: 0;
  font-family: Raleway;
  font-size: 18px;
  font-weight: 600;
  text-align: left;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-family: Raleway;
  font-size: 14px;
  font-weight: 500;
  color: #333;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e1e5e9;
  border-radius: 8px;
  font-family: Raleway;
  font-size: 14px;
  margin-bottom: 24px;
  background: #f8f9fa;

  &:focus {
    outline: none;
    border-color: #9738ff;
    background: white;
  }

  &::placeholder {
    color: #999;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const SubmitButton = styled.button`
  width: 200px;
  padding: 12px 24px;
  border-radius: 8px;
  font-family: Raleway;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  background: #9738ff;
  color: white;

  &:hover {
    background: #7a2dcc;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

interface FolderEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (cardText: string, folderName: string) => void;
  folderName: string;
  existingQuestion?: string;
}

const FolderEditModal: React.FC<FolderEditModalProps> = ({
  isOpen,
  onClose,
  onSave,
  folderName,
  existingQuestion = ''
}) => {
  const [cardText, setCardText] = useState(existingQuestion);
  const [currentFolderName, setCurrentFolderName] = useState(folderName);

  useEffect(() => {
    setCardText(existingQuestion);
    setCurrentFolderName(folderName);
  }, [existingQuestion, folderName]);

  if (!isOpen) return null;

  const handleSubmit = () => {
    if (cardText.trim() && currentFolderName.trim()) {
      onSave(cardText, currentFolderName);
      onClose();
      setCardText('');
      setCurrentFolderName('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  return ReactDOM.createPortal(
    <Overlay open={isOpen} opacity={0.5} onClick={onClose}>
      <div onClick={e => e.stopPropagation()}>
        <StyledModal
          open={true}
          width="500px"
          horizontal="center"
          vertical="center"
          padding="24px"
        >
          <div onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <Title>Edit Folder</Title>
              <CloseButton onClick={onClose}>
                <StyledCloseIcon />
              </CloseButton>
            </ModalHeader>

            <Label htmlFor="folderName">Folder Name</Label>
            <Input
              id="folderName"
              type="text"
              value={currentFolderName}
              onChange={e => setCurrentFolderName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type here..."
              maxLength={60}
              autoFocus
            />

            <Label htmlFor="cardText">Enter Card Text</Label>
            <Input
              id="cardText"
              type="text"
              value={cardText}
              onChange={e => setCardText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type here..."
              maxLength={250}
            />

            <ButtonContainer>
              <SubmitButton
                onClick={e => {
                  e.stopPropagation();
                  handleSubmit();
                }}
              >
                Submit
              </SubmitButton>
            </ButtonContainer>
          </div>
        </StyledModal>
      </div>
    </Overlay>,
    document.body
  );
};

export default FolderEditModal;
